import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart, HeartCrack, User, X, Filter, Trophy } from 'lucide-react';
import { 
  getMostLikedCards, 
  getUserLikedCards, 
  addCardReaction,
  removeCardReaction, 
  CardWithSentiment, 
  ReactionType,
  SortOption,
  FilterOption
} from '../services/pokemonTcgApi';
import { useUserReactions, useReactionActions } from '../lib/reactionStore';
import { useAuthStore, supabase } from '../lib/supabase';
import { formatPrice } from '../utils/formatPrice';
import { useCurrencyStore } from '../lib/currencyStore';
import { slugify } from '../utils/slugify';
import CardImage from '../components/CardImage';
import LoadingHeader from '../components/LoadingHeader';
import Breadcrumbs from '../components/Breadcrumbs';
import { useToast } from '../components/Toast';

// Simple Vote Button Component
interface VoteButtonProps {
  cardId: string;
  voteType: 'like' | 'dislike';
  count: number;
  isActive: boolean;
  onVote: (cardId: string, voteType: 'like' | 'dislike') => void;
  size?: 'sm' | 'md';
}

function VoteButton({ cardId, voteType, count, isActive, onVote, size = 'md' }: VoteButtonProps) {
  const Icon = voteType === 'like' ? Heart : HeartCrack;
  const color = voteType === 'like' ? 'text-green-500' : 'text-red-500';
  const activeColor = voteType === 'like' ? 'bg-green-500/20 border-green-500/50' : 'bg-red-500/20 border-red-500/50';
  
  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-sm' : 'px-3 py-2 text-base';
  const iconSize = size === 'sm' ? 14 : 16;
  
  return (
    <button
      onClick={() => onVote(cardId, voteType)}
      className={`
        flex items-center gap-2 rounded-lg transition-all border ${sizeClasses}
        ${isActive 
          ? `${activeColor} ${color} ring-2 ring-white/10` 
          : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20 hover:text-white'
        }
        cursor-pointer
      `}
      title={voteType === 'like' ? 'Like this card' : 'Dislike this card'}
    >
      <Icon 
        size={iconSize} 
        className={`transition-colors ${color} ${isActive ? 'fill-current' : ''}`}
      />
      {count > 0 && (
        <span className={`font-medium transition-colors ${isActive ? 'text-white' : ''}`}>
          {count}
        </span>
      )}
    </button>
  );
}

export default function CommunityPage() {
  const [topCards, setTopCards] = useState<CardWithSentiment[]>([]);
  const [userLikedCards, setUserLikedCards] = useState<CardWithSentiment[]>([]);
  const userReactions = useUserReactions();
  const reactionActions = useReactionActions();
  const [loading, setLoading] = useState(true);
  const [loadingCommunity, setLoadingCommunity] = useState(true);
  const [loadingPersonal, setLoadingPersonal] = useState(false);
  const [activeTab, setActiveTab] = useState<'community' | 'personal'>('community');
  const [removingReaction, setRemovingReaction] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('most_votes');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [userPoints, setUserPoints] = useState<number>(0);
  const { isAuthenticated, user } = useAuthStore();
  const { addToast } = useToast();
  const { currency } = useCurrencyStore();
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(0);
  const [isRealTimeActive, setIsRealTimeActive] = useState(false);

  useEffect(() => {
    fetchCommunityData();
  }, [sortBy, filterBy]);

  useEffect(() => {
    if (isAuthenticated && user && activeTab === 'personal') {
      fetchPersonalData();
    }
  }, [isAuthenticated, user, activeTab]);

  // Real-time subscription to card_votes table for live ranking updates
  useEffect(() => {
    if (activeTab !== 'community') return;

    let subscription: any;
    let fallbackInterval: NodeJS.Timeout;

    const setupRealtimeSubscription = async () => {
      try {
        subscription = supabase
          .channel('card_votes_changes')
          .on(
            'postgres_changes',
            {
              event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
              schema: 'public',
              table: 'card_votes'
            },
            (payload: any) => {
              console.log('Real-time update received:', payload);
              
              // Debounce rapid updates (minimum 2 seconds between refreshes)
              const now = Date.now();
              if (now - lastRefresh < 2000) {
                console.log('Debouncing real-time update');
                return;
              }
              
              setLastRefresh(now);
              refreshCommunityData();
            }
          )
          .subscribe((status: any) => {
            if (status === 'SUBSCRIBED') {
              console.log('Real-time subscription active for card votes');
              setIsRealTimeActive(true);
              // Clear any fallback polling since real-time is working
              if (fallbackInterval) {
                clearInterval(fallbackInterval);
              }
            } else if (status === 'CHANNEL_ERROR') {
              console.error('Real-time subscription error - falling back to polling');
              setIsRealTimeActive(false);
              
              // Fallback to polling every 10 seconds when real-time fails
              fallbackInterval = setInterval(() => {
                if (activeTab === 'community' && !refreshing) {
                  refreshCommunityData();
                }
              }, 10000);
            } else if (status === 'CLOSED') {
              console.log('Real-time subscription closed');
              setIsRealTimeActive(false);
            }
          });
      } catch (error) {
        console.error('Error setting up real-time subscription:', error);
        setIsRealTimeActive(false);
        
        // Fallback to polling if real-time setup fails
        fallbackInterval = setInterval(() => {
          if (activeTab === 'community' && !refreshing) {
            refreshCommunityData();
          }
        }, 10000);
      }
    };

    setupRealtimeSubscription();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
  }, [activeTab, lastRefresh]);

  const fetchCommunityData = async () => {
    try {
      setLoadingCommunity(true);
      
      // Fetch community data with current sort and filter
      const communityData = await getMostLikedCards(50, sortBy, filterBy);
      setTopCards(communityData);

      // Load user reactions for community cards if authenticated
      if (isAuthenticated && user && communityData.length > 0) {
        await loadUserReactions(communityData);
        
        // Calculate user's engagement stats
        const userLikedData = await getUserLikedCards(user.id);
        setUserPoints(userLikedData.length);
        
        // Find user's highest ranked card position
        const userCardIds = userLikedData.map(card => card.id);
        const userBestRank = communityData.findIndex(card => userCardIds.includes(card.id));
        setUserRank(userBestRank >= 0 ? userBestRank + 1 : null);
      }
    } catch (error) {
      console.error('Error fetching community data:', error);
      addToast('Error loading community data', 'error');
    } finally {
      setLoadingCommunity(false);
      setLoading(false);
    }
  };

  // Refresh community data without showing loading states (for real-time updates)
  const refreshCommunityData = useCallback(async () => {
    if (activeTab !== 'community' || refreshing) return;
    
    try {
      setRefreshing(true);
      
      // Fetch fresh community data
      const communityData = await getMostLikedCards(50, sortBy, filterBy);
      setTopCards(communityData);

      // Load user reactions for community cards if authenticated
      if (isAuthenticated && user && communityData.length > 0) {
        await loadUserReactions(communityData);
        
        // Calculate user's engagement stats
        const userLikedData = await getUserLikedCards(user.id);
        setUserPoints(userLikedData.length);
        
        // Find user's highest ranked card position
        const userCardIds = userLikedData.map(card => card.id);
        const userBestRank = communityData.findIndex(card => userCardIds.includes(card.id));
        setUserRank(userBestRank >= 0 ? userBestRank + 1 : null);
      }
    } catch (error) {
      console.error('Error refreshing community data:', error);
      // Don't show toast for background refresh errors
    } finally {
      setRefreshing(false);
    }
  }, [activeTab, isAuthenticated, user, sortBy, filterBy, refreshing]);

  const fetchPersonalData = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      setLoadingPersonal(true);
      const userData = await getUserLikedCards(user.id);
      setUserLikedCards(userData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      addToast('Error loading your liked cards', 'error');
    } finally {
      setLoadingPersonal(false);
    }
  };

  // Load user reactions using the global store
  const loadUserReactions = async (cards: CardWithSentiment[]) => {
    if (!user) return;

    try {
      const cardIds = cards.map(card => card.id);
      await reactionActions.loadUserReactions(user.id, cardIds);
    } catch (error) {
      console.error('Error loading user reactions:', error);
    }
  };

  const handleReactionChange = (cardId: string, reactionType: ReactionType, isAdding: boolean) => {
    // The ReactionButton already handles the optimistic update to the global store
    // We only need to update the local card stats for UI feedback
    
    // Update the card stats for UI feedback
    const updateCard = (card: CardWithSentiment) => {
      if (card.id !== cardId) return card;
      
      const updatedCard = { ...card };
      const currentUserReactions = userReactions.get(cardId) || [];
      
      // Handle primary reactions (mutually exclusive)
      const primaryReactions = ['like', 'dislike'];
      const isPrimaryReaction = primaryReactions.includes(reactionType);
      
      if (isPrimaryReaction) {
        // If user had a different primary reaction, remove it first
        const existingPrimary = currentUserReactions.find(r => primaryReactions.includes(r));
        if (existingPrimary && existingPrimary !== reactionType) {
          const prevCount = updatedCard.vote_stats.reaction_breakdown[existingPrimary] || 0;
          updatedCard.vote_stats.reaction_breakdown = {
            ...updatedCard.vote_stats.reaction_breakdown,
            [existingPrimary]: Math.max(0, prevCount - 1)
          };
          updatedCard.vote_stats.total_reactions = Math.max(0, updatedCard.vote_stats.total_reactions - 1);
        }
      }
      
      // Update the current reaction
      const currentCount = updatedCard.vote_stats.reaction_breakdown[reactionType] || 0;
      updatedCard.vote_stats.reaction_breakdown = {
        ...updatedCard.vote_stats.reaction_breakdown,
        [reactionType]: Math.max(0, currentCount + (isAdding ? 1 : -1))
      };
      
      // Update total reactions
      updatedCard.vote_stats.total_reactions = Math.max(0, 
        updatedCard.vote_stats.total_reactions + (isAdding ? 1 : -1)
      );
      
      return updatedCard;
    };
    
    if (activeTab === 'community') {
      setTopCards(prevCards => prevCards.map(updateCard));
    } else {
      setUserLikedCards(prevCards => prevCards.map(updateCard));
    }
    
    // Note: Real-time updates will handle the refresh automatically
    // No need for setTimeout refresh anymore
  };

  const handleVote = async (cardId: string, voteType: 'like' | 'dislike') => {
    if (!user) {
      addToast('Please sign in to vote', 'error');
      return;
    }

    try {
      // Optimistically update the store
      const currentReactions = userReactions.get(cardId) || [];
      const hasVote = currentReactions.includes(voteType);
      
      reactionActions.optimisticallyUpdateReaction(cardId, voteType, !hasVote);
      
      // Make API call
      const success = await addCardReaction(user.id, cardId, voteType);
      
      if (success) {
        // Reload user reactions to get the latest state
        await reactionActions.loadUserReactionsForCard(user.id, cardId);
        
        // Refresh the community data to show updated counts
        if (activeTab === 'community') {
          fetchCommunityData();
        } else {
          fetchPersonalData();
        }
        
        addToast(
          hasVote ? `Removed ${voteType}` : `${voteType === 'like' ? 'Liked' : 'Disliked'} card`,
          'success'
        );
      } else {
        // Revert optimistic update on failure
        reactionActions.revertOptimisticUpdate(cardId, voteType, !hasVote);
        addToast('Failed to update vote. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error voting:', error);
      // Revert optimistic update on error
      const currentReactions = userReactions.get(cardId) || [];
      const hasVote = currentReactions.includes(voteType);
      reactionActions.revertOptimisticUpdate(cardId, voteType, !hasVote);
      addToast('Error updating vote', 'error');
    }
  };

  const handleRemoveReaction = async (cardId: string) => {
    if (!user) return;
    
    try {
      setRemovingReaction(cardId);
      
      // Remove all reactions for this card (no specific reaction type = remove all)
      const success = await removeCardReaction(user.id, cardId);
      
      if (success) {
        // Update the global store to remove all reactions for this card
        await reactionActions.loadUserReactionsForCard(user.id, cardId);
        
        // Remove from user's liked cards list
        setUserLikedCards(prev => prev.filter(card => card.id !== cardId));
        
        addToast('All reactions removed successfully', 'success');
      } else {
        addToast('Failed to remove reactions', 'error');
      }
    } catch (error) {
      console.error('Error removing reactions:', error);
      addToast('Error removing reactions', 'error');
    } finally {
      setRemovingReaction(null);
    }
  };

  const generateCardUrl = (card: CardWithSentiment) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    return `/${seriesSlug}/${setSlug}/${cardSlug}`;
  };

  const getRankingBadge = (position: number) => {
    if (position === 1) return { color: 'bg-yellow-500', icon: '🥇' };
    if (position === 2) return { color: 'bg-gray-400', icon: '🥈' };
    if (position === 3) return { color: 'bg-amber-600', icon: '🥉' };
    return { color: 'bg-blue-500', icon: '#' };
  };

  const getSentimentColor = (score: number) => {
    if (score >= 0.8) return 'text-green-400';
    if (score >= 0.6) return 'text-yellow-400';
    return 'text-red-400';
  };

  const formatSentimentScore = (score: number) => {
    return `${Math.round(score * 100)}%`;
  };

  if (loading) {
    return <LoadingHeader />;
  }

        return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Community Sentiment - Pokemon Card Index</title>
        <meta name="description" content="Discover the most loved Pokemon cards in our community. See trending cards, vote on your favorites, and explore community sentiment data." />
        <meta name="keywords" content="pokemon cards, community, sentiment, voting, trending, popular cards" />
      </Helmet>

      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.1); }
          50% { transform: scale(1); }
          75% { transform: scale(1.05); }
        }
        
        .heartbeat {
          animation: heartbeat 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Breadcrumbs />
        
        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="flex bg-black/30 rounded-lg p-1 backdrop-blur-sm">
            <button
              onClick={() => setActiveTab('community')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'community'
                  ? 'bg-white/20 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white/60 hover:text-white'
              }`}
            >
              <span className="text-red-500 heartbeat">♥</span>
              <span>Community Sentiment</span>
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'personal'
                  ? 'bg-white/20 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white/60 hover:text-white'
              }`}
            >
              <User className="w-5 h-5" />
              <span>My Votes</span>
            </button>
          </div>
        </div>

                 {/* Community Rankings */}
         {activeTab === 'community' && (
           <div className="space-y-8">
             {/* Filter Controls */}
            <div className="flex justify-center">
              <div className="flex items-center gap-4 bg-black/20 rounded-lg p-4 backdrop-blur-sm">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  <span>Filters</span>
                </button>
                
                {showFilters && (
                  <div className="flex items-center gap-4">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortOption)}
                      className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="most_votes">Most Votes</option>
                      <option value="most_liked">Most Liked</option>
                      <option value="most_recent">Most Recent</option>
                    </select>
                    
                    <select
                      value={filterBy}
                      onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                      className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Cards</option>
                      <option value="liked">Liked Cards</option>
                      <option value="disliked">Disliked Cards</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            {loadingCommunity ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              </div>
            ) : topCards.length === 0 ? (
              <div className="text-center py-16">
                <Trophy className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white/60 mb-2">No community favorites yet</h3>
                <p className="text-white/40">Be the first to vote on your favorite cards!</p>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Top 3 Podium - Binder Style */}
                {topCards.length >= 3 && (
                  <div className="flex flex-col md:flex-row items-end justify-center gap-4 md:gap-6 mb-8">
                    {/* 2nd Place */}
                    <div className="order-2 md:order-1 w-full md:w-auto">
                      <div className="group relative bg-gradient-to-br from-slate-300/20 to-slate-400/10 rounded-xl p-3 border-2 border-slate-400/40 hover:border-slate-300/60 transition-all duration-300 hover:shadow-xl hover:shadow-slate-400/20 max-w-[200px] mx-auto">
                        {/* Ranking Badge */}
                        <div className="absolute -top-4 -left-4 z-20">
                          <img src="/rankings/pci-pokeball-silver.png" alt="Silver Medal" className="w-12 h-12 object-contain drop-shadow-lg" />
                        </div>
                        
                        {/* Sentiment Badge */}
                        <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold bg-slate-400/80 text-black backdrop-blur-md z-10">
                          {formatSentimentScore(topCards[1].vote_stats.sentiment_score)}
                        </div>
                        
                        {/* Card Image */}
                        <Link to={generateCardUrl(topCards[1])} className="block">
                          <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden">
                            <CardImage
                              src={topCards[1].images.small}
                              alt={topCards[1].name}
                              cardId={topCards[1].id}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </Link>
                        
                        {/* Card Info */}
                        <div className="mt-3 space-y-2">
                          <Link to={generateCardUrl(topCards[1])} className="block hover:text-slate-300 transition-colors text-center">
                            <h4 className="font-bold text-base truncate text-white">{topCards[1].name}</h4>
                            <p className="text-xs text-white/60 truncate">{topCards[1].set.name}</p>
                          </Link>
                          
                          {/* Vote Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleVote(topCards[1].id, 'like')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[1].id)?.includes('like')
                                  ? 'bg-green-500/30 border border-green-500/50 text-green-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                              }`}
                            >
                              <Heart size={14} className={userReactions.get(topCards[1].id)?.includes('like') ? 'fill-current' : ''} />
                              <span>{topCards[1].vote_stats.likes_count}</span>
                            </button>
                            <button
                              onClick={() => handleVote(topCards[1].id, 'dislike')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[1].id)?.includes('dislike')
                                  ? 'bg-red-500/30 border border-red-500/50 text-red-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                              }`}
                            >
                              <HeartCrack size={14} className={userReactions.get(topCards[1].id)?.includes('dislike') ? 'fill-current' : ''} />
                              <span>{topCards[1].vote_stats.dislikes_count}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 1st Place - Larger & Elevated */}
                    <div className="order-1 md:order-2 w-full md:w-auto md:-mb-8">
                      <div className="group relative bg-gradient-to-br from-yellow-400/20 to-amber-500/10 rounded-xl p-4 border-2 border-yellow-400/50 hover:border-yellow-300/70 transition-all duration-300 hover:shadow-2xl hover:shadow-yellow-400/30 max-w-[240px] mx-auto ring-4 ring-yellow-500/20">
                        {/* Ranking Badge */}
                        <div className="absolute -top-5 -left-5 z-20">
                          <img src="/rankings/pci-pokeball-gold.png" alt="Gold Medal" className="w-14 h-14 object-contain drop-shadow-lg" />
                        </div>
                        
                        {/* Crown/Champion indicator */}
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-3xl animate-bounce z-10">👑</div>
                        
                        {/* Sentiment Badge */}
                        <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-sm font-bold bg-yellow-400/90 text-black backdrop-blur-md z-10">
                          {formatSentimentScore(topCards[0].vote_stats.sentiment_score)}
                        </div>
                        
                        {/* Card Image */}
                        <Link to={generateCardUrl(topCards[0])} className="block">
                          <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden">
                            <CardImage
                              src={topCards[0].images.small}
                              alt={topCards[0].name}
                              cardId={topCards[0].id}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </Link>
                        
                        {/* Card Info */}
                        <div className="mt-3 space-y-3">
                          <Link to={generateCardUrl(topCards[0])} className="block hover:text-yellow-300 transition-colors text-center">
                            <h4 className="font-bold text-lg truncate text-white">{topCards[0].name}</h4>
                            <p className="text-sm text-white/60 truncate">{topCards[0].set.name}</p>
                          </Link>
                          
                          {/* Vote Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleVote(topCards[0].id, 'like')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[0].id)?.includes('like')
                                  ? 'bg-green-500/30 border border-green-500/50 text-green-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                              }`}
                            >
                              <Heart size={14} className={userReactions.get(topCards[0].id)?.includes('like') ? 'fill-current' : ''} />
                              <span>{topCards[0].vote_stats.likes_count}</span>
                            </button>
                            <button
                              onClick={() => handleVote(topCards[0].id, 'dislike')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[0].id)?.includes('dislike')
                                  ? 'bg-red-500/30 border border-red-500/50 text-red-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                              }`}
                            >
                              <HeartCrack size={14} className={userReactions.get(topCards[0].id)?.includes('dislike') ? 'fill-current' : ''} />
                              <span>{topCards[0].vote_stats.dislikes_count}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 3rd Place */}
                    <div className="order-3 w-full md:w-auto">
                      <div className="group relative bg-gradient-to-br from-amber-600/20 to-amber-700/10 rounded-xl p-3 border-2 border-amber-600/40 hover:border-amber-500/60 transition-all duration-300 hover:shadow-xl hover:shadow-amber-600/20 max-w-[200px] mx-auto">
                        {/* Ranking Badge */}
                        <div className="absolute -top-4 -left-4 z-20">
                          <img src="/rankings/pci-pokeball-bronze.png" alt="Bronze Medal" className="w-12 h-12 object-contain drop-shadow-lg" />
                        </div>
                        
                        {/* Sentiment Badge */}
                        <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold bg-amber-600/80 text-white backdrop-blur-md z-10">
                          {formatSentimentScore(topCards[2].vote_stats.sentiment_score)}
                        </div>
                        
                        {/* Card Image */}
                        <Link to={generateCardUrl(topCards[2])} className="block">
                          <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden">
                            <CardImage
                              src={topCards[2].images.small}
                              alt={topCards[2].name}
                              cardId={topCards[2].id}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </Link>
                        
                        {/* Card Info */}
                        <div className="mt-3 space-y-2">
                          <Link to={generateCardUrl(topCards[2])} className="block hover:text-amber-400 transition-colors text-center">
                            <h4 className="font-bold text-base truncate text-white">{topCards[2].name}</h4>
                            <p className="text-xs text-white/60 truncate">{topCards[2].set.name}</p>
                          </Link>
                          
                          {/* Vote Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleVote(topCards[2].id, 'like')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[2].id)?.includes('like')
                                  ? 'bg-green-500/30 border border-green-500/50 text-green-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                              }`}
                            >
                              <Heart size={14} className={userReactions.get(topCards[2].id)?.includes('like') ? 'fill-current' : ''} />
                              <span>{topCards[2].vote_stats.likes_count}</span>
                            </button>
                            <button
                              onClick={() => handleVote(topCards[2].id, 'dislike')}
                              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all ${
                                userReactions.get(topCards[2].id)?.includes('dislike')
                                  ? 'bg-red-500/30 border border-red-500/50 text-red-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                              }`}
                            >
                              <HeartCrack size={14} className={userReactions.get(topCards[2].id)?.includes('dislike') ? 'fill-current' : ''} />
                              <span>{topCards[2].vote_stats.dislikes_count}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Binder Grid View */}
                {topCards.length > 3 && (
                  <div className="mt-12">
                    <h3 className="text-xl font-bold text-white/80 mb-6 text-center">More Community Favorites</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                      {topCards.slice(3).map((card, index) => {
                        const rank = index + 4;
                        const sentimentColor = getSentimentColor(card.vote_stats.sentiment_score);
                        const userCardReactions = userReactions.get(card.id) || [];
                        const hasLike = userCardReactions.includes('like');
                        const hasDislike = userCardReactions.includes('dislike');
                        
                        return (
                          <div
                            key={card.id}
                            className="group relative"
                          >
                            {/* Card Container with hover effects */}
                            <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-xl p-2 border border-white/10 hover:border-white/30 transition-all duration-300 hover:shadow-xl hover:shadow-white/10 hover:-translate-y-1">
                              {/* Ranking Badge */}
                              <div className="absolute -top-3 -left-3 z-20">
                                <div className={`
                                  w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg
                                  ${rank <= 10 
                                    ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-black' 
                                    : rank <= 20 
                                      ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-black'
                                      : 'bg-gradient-to-br from-white/20 to-white/10 text-white border border-white/20'
                                  }
                                `}>
                                  {rank}
                                </div>
                              </div>
                              
                              {/* Card Image */}
                              <Link to={generateCardUrl(card)} className="block">
                                <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden">
                                  <CardImage
                                    src={card.images.small}
                                    alt={card.name}
                                    cardId={card.id}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                              </Link>
                              
                              {/* Sentiment Score Badge */}
                              <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold backdrop-blur-md ${
                                card.vote_stats.sentiment_score >= 0.8 
                                  ? 'bg-green-500/80 text-white' 
                                  : card.vote_stats.sentiment_score >= 0.6 
                                    ? 'bg-yellow-500/80 text-black'
                                    : 'bg-red-500/80 text-white'
                              }`}>
                                {formatSentimentScore(card.vote_stats.sentiment_score)}
                              </div>
                              
                              {/* Card Info Overlay */}
                              <div className="mt-2 space-y-2">
                                <Link to={generateCardUrl(card)} className="block hover:text-blue-400 transition-colors">
                                  <h4 className="font-semibold text-sm truncate text-white">{card.name}</h4>
                                  <p className="text-xs text-white/60 truncate">{card.set.name}</p>
                                </Link>
                                
                                {/* Vote Buttons */}
                                <div className="flex gap-2 pt-1">
                                  <button
                                    onClick={() => handleVote(card.id, 'like')}
                                    className={`
                                      flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                      ${hasLike 
                                        ? 'bg-green-500/30 border border-green-500/50 text-green-400' 
                                        : 'bg-white/5 border border-white/10 text-white/60 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                                      }
                                    `}
                                  >
                                    <Heart size={12} className={hasLike ? 'fill-current' : ''} />
                                    <span>{card.vote_stats.likes_count}</span>
                                  </button>
                                  <button
                                    onClick={() => handleVote(card.id, 'dislike')}
                                    className={`
                                      flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                      ${hasDislike 
                                        ? 'bg-red-500/30 border border-red-500/50 text-red-400' 
                                        : 'bg-white/5 border border-white/10 text-white/60 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                                      }
                                    `}
                                  >
                                    <HeartCrack size={12} className={hasDislike ? 'fill-current' : ''} />
                                    <span>{card.vote_stats.dislikes_count}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* My Reactions Tab - Binder Grid View */}
        {activeTab === 'personal' && isAuthenticated && (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <User className="w-6 h-6 text-blue-500" />
                My Collection
              </h2>
              {loadingPersonal && (
                <div className="flex items-center gap-2 text-white/60">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/60 border-t-transparent"></div>
                  <span>Loading...</span>
                </div>
              )}
              {!loadingPersonal && userLikedCards.length > 0 && (
                <div className="text-sm text-white/60 bg-white/5 px-3 py-1.5 rounded-full">
                  {userLikedCards.length} card{userLikedCards.length !== 1 ? 's' : ''} in your collection
                </div>
              )}
            </div>

            {loadingPersonal ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              </div>
            ) : userLikedCards.length === 0 ? (
              <div className="text-center py-16">
                <Heart className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white/60 mb-2">No reactions yet</h3>
                <p className="text-white/40">Start reacting to cards to build your collection!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                {userLikedCards.map((card, index) => {
                  const sentimentColor = getSentimentColor(card.vote_stats.sentiment_score);
                  const hasLike = card.vote_stats.user_reaction === 'like';
                  const hasDislike = card.vote_stats.user_reaction === 'dislike';
                  
                  return (
                    <div
                      key={card.id}
                      className="group relative"
                    >
                      {/* Card Container with hover effects */}
                      <div className={`relative bg-gradient-to-br rounded-xl p-2 border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                        hasLike 
                          ? 'from-green-500/20 to-green-600/10 border-green-500/30 hover:border-green-400/50 hover:shadow-green-500/20' 
                          : hasDislike 
                            ? 'from-red-500/20 to-red-600/10 border-red-500/30 hover:border-red-400/50 hover:shadow-red-500/20'
                            : 'from-white/10 to-white/5 border-white/10 hover:border-white/30 hover:shadow-white/10'
                      }`}>
                        {/* Your Reaction Badge */}
                        <div className="absolute -top-3 -left-3 z-20">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                            hasLike 
                              ? 'bg-gradient-to-br from-green-400 to-green-600' 
                              : hasDislike 
                                ? 'bg-gradient-to-br from-red-400 to-red-600'
                                : 'bg-gradient-to-br from-white/20 to-white/10 border border-white/20'
                          }`}>
                            {hasLike ? (
                              <Heart size={14} className="text-white fill-current" />
                            ) : hasDislike ? (
                              <HeartCrack size={14} className="text-white fill-current" />
                            ) : (
                              <span className="text-white text-xs">?</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Remove Button - Top Right */}
                        <button
                          onClick={() => handleRemoveReaction(card.id)}
                          disabled={removingReaction === card.id}
                          className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                          title="Remove from collection"
                        >
                          {removingReaction === card.id ? (
                            <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                          ) : (
                            <X size={12} />
                          )}
                        </button>
                        
                        {/* Card Image */}
                        <Link to={generateCardUrl(card)} className="block">
                          <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden">
                            <CardImage
                              src={card.images.small}
                              alt={card.name}
                              cardId={card.id}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        </Link>
                        
                        {/* Sentiment Score Badge */}
                        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold backdrop-blur-md ${
                          card.vote_stats.sentiment_score >= 0.8 
                            ? 'bg-green-500/80 text-white' 
                            : card.vote_stats.sentiment_score >= 0.6 
                              ? 'bg-yellow-500/80 text-black'
                              : 'bg-red-500/80 text-white'
                        }`}>
                          {Math.round(card.vote_stats.sentiment_score * 100)}%
                        </div>
                        
                        {/* Card Info */}
                        <div className="mt-2 space-y-2">
                          <Link to={generateCardUrl(card)} className="block hover:text-blue-400 transition-colors">
                            <h4 className="font-semibold text-sm truncate text-white">{card.name}</h4>
                            <p className="text-xs text-white/60 truncate">{card.set.name}</p>
                          </Link>
                          
                          {/* Vote Buttons */}
                          <div className="flex gap-2 pt-1">
                            <button
                              onClick={() => handleVote(card.id, 'like')}
                              className={`
                                flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                ${hasLike 
                                  ? 'bg-green-500/30 border border-green-500/50 text-green-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-green-500/20 hover:border-green-500/30 hover:text-green-400'
                                }
                              `}
                            >
                              <Heart size={12} className={hasLike ? 'fill-current' : ''} />
                              <span>{card.vote_stats.likes_count}</span>
                            </button>
                            <button
                              onClick={() => handleVote(card.id, 'dislike')}
                              className={`
                                flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                ${hasDislike 
                                  ? 'bg-red-500/30 border border-red-500/50 text-red-400' 
                                  : 'bg-white/5 border border-white/10 text-white/60 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-400'
                                }
                              `}
                            >
                              <HeartCrack size={12} className={hasDislike ? 'fill-current' : ''} />
                              <span>{card.vote_stats.dislikes_count}</span>
                            </button>
                          </div>
                          
                          {/* Community Stats */}
                          <div className="text-xs text-white/40 text-center pt-1">
                            {card.vote_stats.total_votes} total vote{card.vote_stats.total_votes !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Sign In Prompt for Personal Tab */}
        {activeTab === 'personal' && !isAuthenticated && (
          <div className="text-center py-16">
            <div className="mb-8">
              <Heart className="w-16 h-16 text-red-500/40 mx-auto mb-4 fill-current" />
              <h3 className="text-2xl font-medium text-white mb-3">Join the Community!</h3>
              <p className="text-white/60 mb-6 max-w-md mx-auto">
                Sign in to like your favorite cards, track your personal preferences, and see how your tastes compare with the community.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 